import React, { useState, useEffect } from 'react';
import { letterTemplatesService, letterGeneratorService } from '../../services/letterService';
import './LetterTemplateManager.css';

const LetterTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    category: 'general'
  });

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'business', label: 'Business' },
    { value: 'legal', label: 'Legal' },
    { value: 'invitation', label: 'Invitation' },
    { value: 'notification', label: 'Notification' },
    { value: 'certificate', label: 'Certificate' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const fetchedTemplates = await letterTemplatesService.getAllTemplates();
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      alert('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.content) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const placeholders = letterGeneratorService.extractPlaceholders(formData.content);
      const templateData = {
        ...formData,
        placeholders
      };

      if (editingTemplate) {
        await letterTemplatesService.updateTemplate(editingTemplate.id, templateData);
      } else {
        await letterTemplatesService.createTemplate(templateData);
      }

      await fetchTemplates();
      resetForm();
      setShowCreateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      content: template.content,
      category: template.category || 'general'
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await letterTemplatesService.deleteTemplate(templateId);
      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      content: '',
      category: 'general'
    });
    setEditingTemplate(null);
  };

  const handleCancel = () => {
    resetForm();
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="letter-template-manager">
        <div className="loading">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="letter-template-manager">
      <div className="header">
        <h1>Letter Templates</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create New Template
        </button>
      </div>

      <div className="templates-grid">
        {templates.map(template => (
          <div key={template.id} className="template-card">
            <div className="template-header">
              <h3>{template.name}</h3>
              <span className={`category-badge ${template.category}`}>
                {categories.find(c => c.value === template.category)?.label || 'General'}
              </span>
            </div>
            
            {template.description && (
              <p className="template-description">{template.description}</p>
            )}
            
            <div className="template-placeholders">
              <strong>Placeholders:</strong>
              <div className="placeholders-list">
                {template.placeholders?.map(placeholder => (
                  <span key={placeholder} className="placeholder-tag">
                    {placeholder}
                  </span>
                )) || <span className="no-placeholders">No placeholders</span>}
              </div>
            </div>
            
            <div className="template-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => handleEdit(template)}
              >
                Edit
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => handleDelete(template.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="empty-state">
          <p>No templates found. Create your first template to get started!</p>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingTemplate ? 'Edit Template' : 'Create New Template'}</h2>
              <button className="modal-close" onClick={handleCancel}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="name">Template Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                >
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <input
                  type="text"
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label htmlFor="content">Template Content *</label>
                <textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows="15"
                  placeholder="Enter your letter template here. Use {{placeholder}} for dynamic content. For example: Dear {{recipient_name}}, This is to inform you that {{message}}."
                  required
                />
              </div>

              <div className="placeholder-info">
                <strong>Detected Placeholders:</strong>
                <div className="placeholders-preview">
                  {letterGeneratorService.extractPlaceholders(formData.content).map(placeholder => (
                    <span key={placeholder} className="placeholder-tag">
                      {placeholder}
                    </span>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterTemplateManager;